import { loadStripe, type Stripe, type StripeElementStyle } from '@stripe/stripe-js';

import { env } from '@/shared/config/env';
import { palette, surfaces } from '@/theme/tokens';

/**
 * The Stripe singleton.
 *
 * `loadStripe` injects a script tag, so calling it per render would add one
 * every time. It is called once at module load and the promise is reused — the
 * pattern Stripe's own docs require.
 *
 * Resolves to `null` when no publishable key is configured, which is a
 * supported state: the portal runs without Stripe and only the add-card form
 * is unavailable. `isStripeConfigured` is what the UI branches on.
 */
export const stripePromise: Promise<Stripe | null> =
  env.VITE_APP_STRIPE_PUBLISHABLE_KEY === ''
    ? Promise.resolve(null)
    : loadStripe(env.VITE_APP_STRIPE_PUBLISHABLE_KEY);

export const isStripeConfigured = env.VITE_APP_STRIPE_PUBLISHABLE_KEY !== '';

/**
 * Stripe Elements are iframes, so they inherit nothing from the page — not the
 * cascade, and not the CSS variables the rest of the app reads its colour from.
 * Every value below has to be restated to match the MUI inputs beside them.
 *
 * They are read from the tokens rather than copied as literals: hand-copied
 * hexes silently keep the old palette when the tokens move, which is exactly
 * how these fields ended up rendering last season's ink.
 *
 * 16px is not cosmetic: anything smaller makes iOS Safari zoom the viewport
 * when the field takes focus, exactly as CLAUDE.md requires of native inputs.
 */
export const elementStyle: StripeElementStyle = {
  base: {
    fontSize: '16px',
    fontFamily: "'Inter Variable', system-ui, sans-serif",
    color: surfaces.text.primary,
    '::placeholder': { color: surfaces.text.disabled },
  },
  invalid: { color: palette.error.main },
};
